import clsx from 'clsx'
import React from 'react'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import { InputProps as MuiInputProps, TextField as MuiTextField, TextFieldProps as MuiTextFieldProps, Typography } from '@material-ui/core'

type Props = Readonly<
    Pick<
        MuiTextFieldProps,
        | "type"
        | "id"
        | "name"
        | "label"
        | "placeholder"
        | "value"
        | "error"
        | "helperText"
        | "autoComplete"
        | "autoFocus"
        | "disabled"
        | "fullWidth"
        | "multiline"
        | "className"
        | "inputProps"
        | "InputLabelProps"
        | "children"
        | "onChange"
        | "onBlur"
    > &
    Pick<MuiInputProps, "startAdornment" | "endAdornment"> & {
        marginBottom?: string | number
        fontSize?: string | number
        placeholder?: string,
        title?: string,
        variant?: "filled" | "standard" | "outlined" | undefined,
    }
>

export default function TextField({
    title,
    placeholder,
    variant,
    startAdornment,
    endAdornment,
    marginBottom,
    fontSize,
    className,
    inputProps = {},
    children,
    ...rest
}: Props) {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                width: "100%",
                marginBottom: marginBottom,
                fontSize: fontSize ? fontSize : "0.625rem",  // = 10px @ std
            },
            mb_1: {
                marginBottom: theme.spacing(1),
            },
        })
    )

    const classes = useStyles()

    return (
        <>
            {title ?
                <Typography variant="body1" className={classes.mb_1}>
                    {title}
                </Typography>
                : null}
            <MuiTextField
                variant={variant || "standard"}
                className={clsx(classes.root, className)}
                inputProps={{
                    ...inputProps,
                    placeholder: placeholder || '',
                }}
                InputProps={{ startAdornment, endAdornment }}
                {...rest}>
                {children}
            </MuiTextField>
        </>
    )
}


type TextAreaProps = {
    placeholder?: string,
    title?: string,
    characterLimit?: number,
    minRows?: number,
    maxRows?: number,
    value: string
} & MuiTextFieldProps
function TextArea(props: TextAreaProps) {
    const { characterLimit = 250, value = '', ...rest } = props

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            mb_1: {
                marginBottom: theme.spacing(1),
            },
        })
    )

    const classes = useStyles()

    return (
        <>
            {props.title ?
                <Typography variant="body1" className={classes.mb_1}>
                    {props.title}
                </Typography>
             : null}
            <MuiTextField
                multiline
                minRows={props.minRows || 4}
                maxRows={props.maxRows || 100}
                variant="outlined"
                inputProps={{
                    placeholder: props.placeholder || '',
                    maxLength: characterLimit
                }}
                value={value}
                helperText={<Typography style={{ display: "block" }} component="span" variant='body2' align='right'>{value.length}/{characterLimit}</Typography>}
                fullWidth
                {...rest}
            />
        </>
    )
}

export {
    TextField,
    TextArea
}
